import { useState } from 'react';

import { Lightbulb, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import SectionCard from '../SectionCard';
import { ThemeTooltip } from '../TooltipModals';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { Skeleton } from '@/components/atoms/Skeleton';
import Modal from '@/components/molecules/Modals/CustomModal';
import { Fact, Summary } from '@/types';

interface ThemesSectionProps {
  themes: Summary[];
  keyFacts: Fact[] | undefined;
  isLoading?: boolean;
  caseId?: string;
  onExpandTheme?: (theme: Summary) => void;
}

export const ThemesSection = ({ themes, keyFacts, isLoading = false, caseId, onExpandTheme }: ThemesSectionProps) => {
  const navigate = useNavigate();
  const [tooltipModalOpen, setTooltipModalOpen] = useState(false);
  const [tooltipModalContent, setTooltipModalContent] = useState(<></>);

  const { data: themesWithCounts = [] } = useGetCaseThemes(caseId || null);

  const themeCountMap = themesWithCounts.reduce<Record<string, number>>((acc, theme) => {
    acc[theme.name] = theme.count;

    return acc;
  }, {});

  const handleExploreFactsClick = (themeName: string) => {
    if (!caseId) return;
    const searchParams = new URLSearchParams();
    searchParams.set('caseId', caseId);
    searchParams.set('themes', themeName);
    navigate(`/app/chronos/case-editor/data/facts?${searchParams.toString()}`);
  };

  return (
    <>
      <SectionCard
        title="High-Level Case Themes"
        onInfoClick={() => {
          setTooltipModalContent(<ThemeTooltip />);
          setTooltipModalOpen(true);
        }}
        headerActions={<div className="text-sm text-blue-500">({themes.length})</div>}
        className="h-auto overflow-y-auto lg:max-h-[420px]"
      >
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[...Array(2)].map((_, index) => (
              <div
                key={index}
                className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 shadow-sm"
                style={{ borderLeft: '4px solid #3b82f6' }}
              >
                <Skeleton className="mt-1 h-4 w-4 bg-blue-200" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <Skeleton className="h-4 w-3/4 bg-blue-200" />
                  <Skeleton className="h-3 w-full bg-blue-200" />
                </div>
              </div>
            ))}
          </div>
        ) : !themes.length ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">No themes available</div>
        ) : (
          <div className="flex flex-col gap-3">
            {themes.map((theme, index) => {
              const factCount = themeCountMap[theme.name] || 0;
              return (
                <div
                  key={index}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 shadow-sm transition-all hover:bg-blue-100 hover:shadow-md"
                  style={{ borderLeft: '4px solid #3b82f6' }} // Tailwind blue-500
                  onClick={() => {
                    onExpandTheme?.(theme);
                    trackCustomEvent(TrackingEvents.ViewFullThemesOnSummary);
                  }}
                >
                  <Lightbulb className="mt-1 h-4 w-4 text-blue-400" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-semibold text-blue-700">{theme.name}</span>
                    <span className="truncate text-xs text-blue-600">
                      {theme.content?.split('\n')[0]?.slice(0, 80) || ''}
                      {theme.content && theme.content.length > 80 ? '…' : ''}
                    </span>
                  </div>
                  {factCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExploreFactsClick(theme.name);
                      }}
                      className="flex items-center gap-2 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 shadow-sm transition-all hover:bg-blue-50 hover:shadow-md"
                    >
                      <Search className="h-3 w-3" />
                      <span>Explore {factCount} Facts</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Tooltip Modal */}
      <Modal handleClose={() => setTooltipModalOpen(false)} isOpen={tooltipModalOpen} content={tooltipModalContent} />
    </>
  );
};
